import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import classnames from 'classnames';

import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ButtonBase from '@mui/material/ButtonBase';

import EqualizerIcon from '@mui/icons-material/Equalizer';
import WorkIcon from '@mui/icons-material/Work';

import withConfirmAction from '@ts-core/components/HOC/withConfirmAction';
import { RankingType, CourseStatus } from '@ts-core/utils/Constants';

import Typography from '@ts-core/components/Typography';
import CourseCardMenu from './CourseCardMenu';

const useStyles = makeStyles((theme) => ({
  root: {
    transition: theme.transitions.create('box-shadow', {
      duration: theme.transitions.duration.standard,
    }),
    '&:hover': {
      boxShadow: theme.shadows[3],
    },
  },
  button: {
    display: 'block',
    minHeight: 96,
    flexGrow: 1,
    backgroundColor: theme.palette.grey[200],
  },
  closedCourse: {
    backgroundColor: theme.palette.secondary.main,
  },
  openCourse: {
    backgroundColor: '#2FA865',
  },
  studentsCount: {
    marginTop: theme.spacing(1),
  },
  menu: {
    position: 'absolute',
    top: 10,
    right: 8,
  },
}));

const CourseCard = (props) => {
  const classes = useStyles();
  const {
    course,
    onSelectCourse,
    onEditCourse,
    onRemoveCourse,
    loading,
  } = props;

  const cardClass =
    course.periodStatus === CourseStatus.Active
      ? classes.openCourse
      : classes.closedCourse;

  const handleSelectCourse = useCallback(() => {
    onSelectCourse(course);
  }, [onSelectCourse, course]);

  return (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
      borderRadius="8px"
      boxShadow={loading ? 0 : 1}
      className={classnames({ [classes.root]: !loading })}
      overflow="hidden"
      position="relative"
      color="white"
    >
      <ButtonBase
        disabled={loading}
        component="div"
        onClick={handleSelectCourse}
        disableRipple
        className={classnames(classes.button, { [cardClass]: !loading })}
      >
        <Box
          pl={2}
          pr={4}
          py={1}
          flexGrow={1}
          color="white"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          height="100%"
        >
          <Typography
            variant="h6"
            color="inherit"
            loading={loading}
            skeletonWidth="30%"
            skeletonHeight={24}
          >
            {course.name}
          </Typography>
          {course.students > 0 && (
            <Typography
              variant="body2"
              color="inherit"
              className={classes.studentsCount}
            >
              <FormattedMessage
                id="course.numberOfStudents"
                values={{ students: course.students }}
              />
            </Typography>
          )}
        </Box>
      </ButtonBase>
      {!loading && (
        <CourseCardMenu
          className={classes.menu}
          course={course}
          onEditCourse={onEditCourse}
          onRemoveCourse={onRemoveCourse}
        />
      )}
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        height={48}
        bgcolor={loading ? 'grey.100' : 'white'}
      >
        {!loading && (
          <>
            <Tooltip
              title={<FormattedMessage id="button.assignments.tooltip" />}
            >
              <IconButton component={Link} to={`${course._id}/assignments`} size="large">
                <WorkIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={<FormattedMessage id="button.ranking.tooltip" />}>
              <IconButton
                component={Link}
                to={`${course._id}/rankings/${RankingType.general}`}
                size="large">
                <EqualizerIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
};

CourseCard.propTypes = {
  course: PropTypes.object,
  loading: PropTypes.bool,

  onSelectCourse: PropTypes.func.isRequired,
  onEditCourse: PropTypes.func,
  onRemoveCourse: PropTypes.func,
};

CourseCard.defaultProps = {
  course: {},
  loading: false,
  onEditCourse: undefined,
  onRemoveCourse: undefined,
};

export default withConfirmAction(CourseCard);
